import React, { useEffect, useState } from "react";
import type { AdminSeason } from "../api/types";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import { useCreateAdminSeasonMutation, useUpdateAdminSeasonMutation } from "../api/gameApi";
import { errorText, transitionConfirm } from "./adminText";
import {
  REAL_SEASON_FORM,
  createBody,
  formFromSeason,
  patchBody,
  type SeasonFormState,
  type SpecialDayDraft,
} from "./seasonFormModel";
import {
  Btn,
  CheckLabel,
  Input,
  Label,
  Notice,
  Row,
  Section,
  SectionTitle,
  Select,
  Small,
  SubTitle,
  TextArea,
} from "./adminStyles";

const PLACE_NAMES = ["1st", "2nd", "3rd", "4th", "5th"];

type Props = { mode: "create" } | { mode: "edit"; season: AdminSeason };

/** Create the season (prefilled with the real one) or edit it. */
const SeasonForm: React.FC<Props> = (props) => {
  const season = props.mode === "edit" ? props.season : null;
  const confirm = useConfirm();
  const [form, setForm] = useState<SeasonFormState>(() => (season ? formFromSeason(season) : REAL_SEASON_FORM));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [createSeason, { isLoading: creating }] = useCreateAdminSeasonMutation();
  const [updateSeason, { isLoading: updating }] = useUpdateAdminSeasonMutation();
  const busy = creating || updating;

  // a different season, or the same one saved from elsewhere: start from what is stored
  const seasonKey = season ? `${season.id}` : "new";
  useEffect(() => {
    if (season) setForm(formFromSeason(season));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonKey]);

  const set = <K extends keyof SeasonFormState>(key: K, value: SeasonFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(null);
  };
  const setSpecial = (i: number, patch: Partial<SpecialDayDraft>) =>
    set(
      "specialDays",
      form.specialDays.map((d, j) => (j === i ? { ...d, ...patch } : d)),
    );

  const datesLocked = !!season?.datesLocked;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(null);
    try {
      if (!season) {
        const body = createBody(form);
        // announced or open goes public on /game and the home page right away and
        // arms the teasers, so it asks first, like the same move on the lifecycle panel
        if (body.status === "announced" || body.status === "open") {
          const c = transitionConfirm(
            // only the end move reads currentDayKey
            { dayKeyFrom: form.dayKeyFrom, dayKeyTo: form.dayKeyTo, opensAt: body.opensAt ?? null, currentDayKey: "" },
            body.status,
          );
          const ok = await confirm({
            title: body.status === "open" ? "Create the season with joining open?" : "Create and announce the season?",
            message: c.message,
            confirmLabel: "Create",
            tone: c.tone,
          });
          if (!ok) return;
        }
        await createSeason(body).unwrap();
        return;
      }
      const body = patchBody(form, season);
      if (Object.keys(body).length === 0) {
        setSaved("Nothing changed.");
        return;
      }
      const res = await updateSeason({ id: season.id, body }).unwrap();
      const n = res.resettledDays;
      setSaved(
        n == null
          ? "Saved. No days needed re-settling."
          : `Saved. Re-settled ${n} ${n === 1 ? "day" : "days"} with the new scoring.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : errorText(err, "Could not save the season."));
    }
  };

  return (
    <Section aria-label={season ? "Edit the season" : "Create the season"}>
      <SectionTitle>{season ? "Edit the season" : "Create the season"}</SectionTitle>
      {!season && (
        <Small style={{ marginBottom: 12 }}>
          Prefilled with the 2026 season. It is created straight on the server; check the special days and the prize
          before you create it.
        </Small>
      )}
      <form onSubmit={submit} noValidate>
        <Row>
          <Label>
            Name
            <Input className="gadm-input" value={form.name} maxLength={80} onChange={(e) => set("name", e.target.value)} />
          </Label>
          {!season && (
            <Label>
              Status on create
              <Select
                value={form.status}
                onChange={(e) => set("status", e.target.value as SeasonFormState["status"])}
              >
                <option value="draft">draft (hidden)</option>
                <option value="announced">announced (teaser, no joining)</option>
                <option value="open">open (joining open)</option>
              </Select>
            </Label>
          )}
        </Row>
        <Row>
          <Label>
            Description
            <TextArea value={form.description} maxLength={500} onChange={(e) => set("description", e.target.value)} />
          </Label>
        </Row>

        <SubTitle>Dates</SubTitle>
        {datesLocked && <Small style={{ marginBottom: 8 }}>The season has started, so the dates are locked.</Small>}
        <Row>
          <Label>
            First race (AOTD day)
            <Input
              type="date"
              aria-label="First day"
              value={form.dayKeyFrom}
              disabled={datesLocked}
              onChange={(e) => set("dayKeyFrom", e.target.value)}
            />
          </Label>
          <Label>
            Last race (AOTD day)
            <Input
              type="date"
              aria-label="Last day"
              value={form.dayKeyTo}
              disabled={datesLocked}
              onChange={(e) => set("dayKeyTo", e.target.value)}
            />
          </Label>
          <Label>
            Joining opens at (your time, optional)
            <Input
              type="datetime-local"
              aria-label="Joining opens at"
              value={form.opensAt}
              onChange={(e) => set("opensAt", e.target.value)}
            />
          </Label>
        </Row>

        <SubTitle>Points</SubTitle>
        <Row>
          {form.place.map((p, i) => (
            <Label key={i} style={{ minWidth: 70 }}>
              {PLACE_NAMES[i]}
              <Input
                inputMode="numeric"
                aria-label={`Points for ${PLACE_NAMES[i]}`}
                value={p}
                onChange={(e) =>
                  set(
                    "place",
                    form.place.map((x, j) => (j === i ? e.target.value : x)),
                  )
                }
              />
            </Label>
          ))}
          <Label style={{ minWidth: 70 }}>
            Raced
            <Input inputMode="numeric" aria-label="Points for racing" value={form.raced} onChange={(e) => set("raced", e.target.value)} />
          </Label>
          <Label style={{ minWidth: 70 }}>
            Guessing win
            <Input
              inputMode="numeric"
              aria-label="Points for the guessing"
              value={form.guessWin}
              onChange={(e) => set("guessWin", e.target.value)}
            />
          </Label>
        </Row>
        <Row>
          <Btn type="button" disabled={form.place.length >= 5} onClick={() => set("place", [...form.place, "0"])}>
            Add a place
          </Btn>
          <Btn type="button" disabled={form.place.length <= 1} onClick={() => set("place", form.place.slice(0, -1))}>
            Remove the last place
          </Btn>
        </Row>

        <SubTitle>Special days</SubTitle>
        <Small style={{ marginBottom: 8 }}>
          The multiplier applies to the whole day's points for everyone. Labels go into notifications: no emoji and no
          word starting with "/".
        </Small>
        {form.specialDays.map((d, i) => (
          <Row key={i} role="group" aria-label={`Special day ${i + 1}`}>
            <Label>
              Day
              <Input
                type="date"
                aria-label={`Special day ${i + 1} date`}
                value={d.dayKey}
                min={form.dayKeyFrom || undefined}
                max={form.dayKeyTo || undefined}
                onChange={(e) => setSpecial(i, { dayKey: e.target.value })}
              />
            </Label>
            <Label>
              Label
              <Input
                aria-label={`Special day ${i + 1} label`}
                value={d.label}
                maxLength={40}
                onChange={(e) => setSpecial(i, { label: e.target.value })}
              />
            </Label>
            <Label style={{ minWidth: 90, flex: "0 1 110px" }}>
              Multiplier
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max="5"
                aria-label={`Special day ${i + 1} multiplier`}
                value={d.multiplier}
                onChange={(e) => setSpecial(i, { multiplier: e.target.value })}
              />
            </Label>
            <Btn
              type="button"
              variant="danger"
              onClick={() =>
                set(
                  "specialDays",
                  form.specialDays.filter((_, j) => j !== i),
                )
              }
            >
              Remove
            </Btn>
          </Row>
        ))}
        <Row>
          <Btn
            type="button"
            onClick={() => set("specialDays", [...form.specialDays, { dayKey: "", label: "", multiplier: "2" }])}
          >
            Add a special day
          </Btn>
        </Row>

        <SubTitle>Prize and rules</SubTitle>
        <Row>
          <Label>
            Prize text (goes into notifications: no emoji, no "/word")
            <Input value={form.prizeText} maxLength={200} onChange={(e) => set("prizeText", e.target.value)} />
          </Label>
        </Row>
        <Row>
          <Label>
            Rules text (optional, shown under the points table)
            <TextArea value={form.rulesText} maxLength={1000} onChange={(e) => set("rulesText", e.target.value)} />
          </Label>
        </Row>
        <CheckLabel>
          <input
            type="checkbox"
            checked={form.countDaysBeforeJoin}
            onChange={(e) => set("countDaysBeforeJoin", e.target.checked)}
          />
          <span>Late joiners keep the days raced before they joined</span>
        </CheckLabel>
        <CheckLabel>
          <input
            type="checkbox"
            checked={form.reachRequiresInstalledApp}
            onChange={(e) => set("reachRequiresInstalledApp", e.target.checked)}
          />
          <span>Push counts only with the site installed as an app (Telegram always counts)</span>
        </CheckLabel>
        {season && (
          <Small style={{ marginBottom: 8 }}>
            Changing the points, the special days or the late-joiner rule re-settles every settled day. It sends no
            messages.
          </Small>
        )}

        {error && (
          <Notice tone="error" role="alert">
            {error}
          </Notice>
        )}
        {saved && (
          <Notice tone="ok" role="status">
            {saved}
          </Notice>
        )}
        <Row>
          <Btn type="submit" variant="primary" disabled={busy}>
            {season ? (busy ? "Saving…" : "Save") : busy ? "Creating…" : "Create the season"}
          </Btn>
        </Row>
      </form>
    </Section>
  );
};

export default SeasonForm;
